import React, { Fragment, useCallback, useEffect, useState } from "react";

import { FormattedMessage } from "react-intl";

import useDecodedParams from "Hooks/useDecodedParams";
import { capitalize } from "Libs/utils";
import { getAutoscalingConfigurationThunk } from "Reducers/environment/service/service.slice";
import {
  EmptyMetrics,
  GenericError
} from "src/organization/pages/project/environment/metrics/components/Errors";
import TimeframeControl from "src/organization/pages/project/environment/metrics/components/timeframeControl/TimeframeControl";
import Service from "src/organization/pages/project/environment/metrics/containers/Service";
import useActivityToggle from "src/organization/pages/project/environment/metrics/hooks/useActivityToggle";
import useInitialTimeframe from "src/organization/pages/project/environment/metrics/hooks/useInitialTimeframe";
import { MetricsContext } from "src/organization/pages/project/environment/metrics/hooks/useMetricsContext";
import usePinnedTabsSync from "src/organization/pages/project/environment/metrics/hooks/usePinnedTabsSync";
import useStickyTimecontrol from "src/organization/pages/project/environment/metrics/hooks/useStickyTimecontrol";
import * as V from "src/organization/pages/project/environment/metrics/resources/views.styles";
import { getPollInterval } from "src/organization/pages/project/environment/metrics/utils/polling/utils";
import { useAppDispatch } from "Store/hooks";

import UsageView from "../totalUsage/Page";
import { TimeFrameType } from "../types";

import * as S from "./Grid.styles";
import GridSkeleton from "./GridSkeleton";
import InstanceSelector from "./InstanceSelector";
import usePollLegacy from "./usePoll.legacy";

const Grid = () => {
  const dispatch = useAppDispatch();
  const [timeFrame, setTimeFrame] = useState(useInitialTimeframe());
  const [isDeployment, setIsDeployment] = useActivityToggle();
  const { projectId, environmentId } = useDecodedParams();

  const [currentInstance, setCurrentInstance] = useState({});

  const pollInterval = getPollInterval(timeFrame);
  const { data, error, instances, lastUpdated, status } = usePollLegacy({
    timeFrame
  });

  const onBrush = useCallback((timeFrame: TimeFrameType) => {
    setTimeFrame(timeFrame);
  }, []);

  const webAppStart = data?.services.findIndex(
    service => service.kind === "webapp"
  );
  const serviceStart = data?.services.findIndex(
    service => service.kind === "service"
  );
  const workerStart = data?.services.findIndex(
    service => service.kind === "worker"
  );
  const firstPerSection = [webAppStart, serviceStart, workerStart];
  const services = data?.services;
  useEffect(() => {
    dispatch(getAutoscalingConfigurationThunk({ projectId, environmentId }));
  }, [dispatch, projectId, environmentId]);

  const { onSyncChange, isSynced, canJoinSync } = usePinnedTabsSync({
    setTimeFrame: setTimeFrame,
    timeFrame: timeFrame
  });

  const handleSyncChange = (isSynced: boolean) => {
    onSyncChange(isSynced, timeFrame);
  };
  const stickyRef = useStickyTimecontrol();

  if (error) {
    return <GenericError error={error} defaultHeader={false} />;
  }

  return (
    <MetricsContext.Provider
      value={{
        isDeployment,
        timeFrame,
        setTimeFrame
      }}
    >
      <V.Header ref={stickyRef}>
        <V.Heading tag="h2">
          <FormattedMessage id="metrics.grid.heading" />
        </V.Heading>

        <TimeframeControl
          isUpdating={status.isLoading}
          timeframe={{
            onChange: setTimeFrame,
            timeframe: timeFrame
          }}
          pollInterval={pollInterval}
          lastUpdated={lastUpdated}
          deployment={{ enabled: isDeployment, onChange: setIsDeployment }}
          tabSync={{
            onSyncChange: handleSyncChange,
            isSynced,
            canJoinSync
          }}
        />
      </V.Header>

      <S.Layout>
        {status.refresh && !services ? (
          <GridSkeleton />
        ) : (
          <>
            {services && <UsageView services={services} status={status} />}
            {services?.map((service, i) => {
              const selectedInstances =
                currentInstance[service.id] || instances?.[service.id] || [];

              const instanceFilter = (
                <InstanceSelector
                  key={service.id}
                  instances={instances?.[service.id]}
                  selected={selectedInstances}
                  onChange={v => {
                    setCurrentInstance(prev => ({ ...prev, [service.id]: v }));
                  }}
                />
              );

              const sectionLabel =
                service.kind === "webapp"
                  ? "Applications"
                  : `${capitalize(service.kind)}s`;
              return (
                <Fragment key={service.id}>
                  {firstPerSection.includes(i) && (
                    <S.SectionHeader>{sectionLabel}</S.SectionHeader>
                  )}
                  <Service
                    service={service}
                    onBrush={onBrush}
                    hosts={selectedInstances}
                    isLoading={status.refresh}
                    kind="grid"
                    actions={instanceFilter}
                  />
                </Fragment>
              );
            })}
          </>
        )}
        {!services && !status.isLoading && <EmptyMetrics />}
      </S.Layout>
    </MetricsContext.Provider>
  );
};

export default React.memo(Grid);
